import {
  useState,
  useRef,
  useEffect,
  type FormEvent,
  forwardRef,
  useImperativeHandle,
} from "react";

interface ChatInputProps {
  onSendMessage: (content: string) => void;
  onTypingStart: () => void;
  onTypingStop: () => void;
  typingPlayers: string[];
  disabled?: boolean;
}

export const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(
  (
    {
      onSendMessage,
      onTypingStart,
      onTypingStop,
      typingPlayers,
      disabled = false,
    },
    ref,
  ) => {
    const [message, setMessage] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
      undefined,
    );
    const inputRef = useRef<HTMLTextAreaElement | null>(null);

    // Expose the internal ref to the parent via forwardRef
    useImperativeHandle(ref, () => inputRef.current as HTMLTextAreaElement);

    // Handle typing indicator
    useEffect(() => {
      return () => {
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
      };
    }, []);

    const handleInputChange = (value: string) => {
      setMessage(value);

      // Start typing indicator
      if (!isTyping && value.length > 0) {
        setIsTyping(true);
        onTypingStart();
      }

      // Reset typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      if (value.length > 0) {
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false);
          onTypingStop();
        }, 3000);
      } else {
        setIsTyping(false);
        onTypingStop();
      }
    };

    const handleSubmit = (e: FormEvent) => {
      e.preventDefault();

      const trimmedMessage = message.trim();
      if (!trimmedMessage || disabled) return;

      onSendMessage(trimmedMessage);
      setMessage("");
      setIsTyping(false);
      onTypingStop();

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Focus back on input
      inputRef.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Send on Enter (without shift)
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    };

    const formatTypingIndicator = () => {
      if (typingPlayers.length === 0) return "";
      if (typingPlayers.length === 1) {
        return `${typingPlayers[0]} is typing...`;
      }
      if (typingPlayers.length === 2) {
        return `${typingPlayers[0]} and ${typingPlayers[1]} are typing...`;
      }
      return `${typingPlayers.length} people are typing...`;
    };

    return (
      <div className="border-t border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
        {/* Typing indicator */}
        <div className="h-5 mb-2">
          {typingPlayers.length > 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic animate-pulse">
              {formatTypingIndicator()}
            </p>
          )}
        </div>

        {/* Input form */}
        <form onSubmit={handleSubmit} className="flex gap-2">
          <textarea
            ref={inputRef}
            value={message}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            disabled={disabled}
            rows={1}
            maxLength={500}
            className="flex-1 resize-none px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={disabled || !message.trim()}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="chat-send-button"
          >
            Send
          </button>
        </form>

        {/* Character count */}
        <div className="text-right mt-1">
          <span
            className={`text-xs ${
              message.length > 450
                ? "text-red-500"
                : "text-gray-400 dark:text-gray-500"
            }`}
          >
            {message.length}/500
          </span>
        </div>
      </div>
    );
  },
);

ChatInput.displayName = "ChatInput";
